import * as globals from '../globals';
import { FrustumIntersector } from './FrustumIntersector';
import THREE from "three";
import { ModelExploder } from "./ModelExploder";
import { RENDER_BUDGET_PER_BATCH, RENDER_BUDGET_SCALING_FACTOR_WHILE_LOADING, USE_GEOMETRY_OOC } from '../globals';
import { ConsolidatedRenderBatch } from "./RenderBatch";
import { mainLoop } from "../../application/MainLoop";
import {
  createLegacyBatchTimingState,
  deductLegacyBatchTimeCost,
  measureBatchDelta } from
'./LegacyRenderBatchTiming';

/** @import { RenderBatch } from "./RenderBatch" */
/** @import { RenderModel } from "./RenderModel" */

/*
 * Keep these threeJs objects file local for performance reasons,
 * as these are changed frequently, so we keep expensive object creation minimal.
 */
const tmpBox = new THREE.Box3(); // Reused for return values of getVisibleBounds()

// Sentinel returned when the render budget is forcibly exhausted (deadline hit, interrupted batch, etc.).
const BUDGET_EXHAUSTED = -0.0001;

/**
 * RenderScene
 * Represents the full graphical scene.
 * Used for iterating through the scene for progressive rendering,
 * hit testing, etc.
 */
export class RenderScene {
  /** @type {Viewer3D} */
  #viewer;

  // true indicates that progressive rendering has finished
  // since last reset call, i.e. all batches have been traversed.
  #done = false;

  // Whether all opaque batches have finished drawing
  #doneOpaques = false;
  // Whether we need to re-draw all transparent batches after all the opaque
  // batches have finished, in order to finish rendering the frame.
  #transparentsNeedRedraw = false;

  /** @type {RenderModel[]} - All RenderModels to be rendered. */
  #models = [];

  /** @type {RenderBatch[]} - points to the next batch to be rendered from _models[i]. Same length as _models. */
  #candidateRenderBatches = [];

  /** @type {RenderBatch[]} - points to the previous batch rendered from _models[i]. Same length as _models. */
  #previousRenderBatches = [];

  /** @type {RenderModel[]} - All models that are currently loaded, but excluded from rendering/selection etc. */
  #hiddenModels = [];

  // updated for current camera in this.reset().
  #frustum = new FrustumIntersector();

  #raycaster = new THREE.Raycaster();

  /** @type {RenderBatch|undefined} A renderbatch that got interrupted in the last pass and did not yet finish*/
  interruptedRenderBatch = undefined;

  // WBOIT order-independent transparency scheduling. Active strategy ("deferred"): during the progressive pass
  // the transparent BVH root is primed into the same screen-area priority heap as the opaque root, so transparents
  // draw interleaved with opaques against partial depth (the "combined pass"). Once opaques finish, a dedicated
  // correction pass redraws all transparents against the now-complete depth, making the final image depth-correct
  // regardless of draw order. The fields below hold the per-frame state this needs (accum reuse, clear bookkeeping,
  // interleave skip).
  //
  // @todo Alternative strategies worth evaluating unless we focus on single-frame rendering instead (preferred):
  //   - "advance": same interleave, but skip the correction pass. Cheaper, keeps the approximate-depth result.
  //   - "sticky": each tick redraws the most screen-relevant transparent batches against the latest depth, until
  //     no opaques remain; then the remaining transparents are drawn once. Spreads correction across ticks.

  // Set in onClear() when clearMainTargets() fires (full invalidation), consumed in reset().
  // Used to distinguish a full re-render (accum freshly cleared) from a silent re-render
  // (accum still holds the previous frame's correct transparency, so don't trigger correction).
  #mainTargetsClearedBeforeReset = false;
  // When true, the WBOIT accum from the previous frame is still valid (same view, geometry settled)
  // and is reused as-is: no transparent traversal, no correction clear. Set per-frame in reset().
  // This is the persistent blend source for hover/selection recomposites; clearing it strands them empty.
  #reuseTransparentAccum = false;

  // WBOIT non-progressive optimization: when progressive rendering is off, the whole frame renders in a
  // single tick, so the interleaved transparent preview buys nothing and only causes opaque<->transparent
  // pipeline thrashing in the combined pass. When this is set, the transparent root is NOT primed into the
  // combined heap (no interleave); the dedicated correction pass still runs, giving a clean opaque phase
  // followed by a transparent phase within the one tick. Set by RenderCommandSystem before reset().
  #skipInterleavedTransparents = false;

  // indicator that we are in the first frame of progressive rendering
  #isFirstProgressiveFrame = false;

  // are all models loaded? updates each reset
  #allModelsLoaded = false;

  // while loading, we limit the render budget. Controlled via setRenderBudgetScalingEnabled()
  #renderBudgetScalingEnabled = false;

  constructor(viewer) {
    this.#viewer = viewer;
  }

  frustum() {
    return this.#frustum;
  }

  #findById(models, modelId) {
    for (let i = 0; i < models.length; i++) {
      const model = models[i];
      if (model && model.id === modelId) {
        return model;
      }
    }
    return null;
  }

  findModel(modelId) {
    return this.#findById(this.#models, modelId);
  }

  findHiddenModel(modelId) {
    return this.#findById(this.#hiddenModels, modelId);
  }

  addModel(renderModel) {
    if (this.#models.indexOf(renderModel) !== -1) {
      return;
    }

    this.#models.push(renderModel);
    this.#candidateRenderBatches.length = this.#models.length;
    this.#previousRenderBatches.length = this.#models.length;
    this.recomputeLinePrecision();
  }

  removeModel(renderModel) {
    const idx = this.#models.indexOf(renderModel);
    if (idx >= 0) {
      this.#models.splice(idx, 1);
    }

    this.#candidateRenderBatches.length = this.#models.length;
    this.#previousRenderBatches.length = this.#models.length;
    this.recomputeLinePrecision();

    return idx >= 0;
  }

  addHiddenModel(renderModel) {
    const idx = this.#hiddenModels.indexOf(renderModel);
    if (idx < 0) {
      this.#hiddenModels.push(renderModel);
    }
    return idx < 0;
  }

  removeHiddenModel(renderModel) {
    const idx = this.#hiddenModels.indexOf(renderModel);
    if (idx >= 0) {
      this.#hiddenModels.splice(idx, 1);
    }
    return idx >= 0;
  }

  isEmpty() {
    return this.#models.length === 0;
  }

  recomputeLinePrecision() {
    let value = 1;
    const sizeTarget = new THREE.Vector3();

    for (let i = 0, len = this.#models.length; i < len; ++i) {
      const modelBox = this.#models[i].getData().bbox;

      // Skip empty boxes, as they lead to a zero threshold
      if (modelBox.getSize(sizeTarget).length() === 0) {
        continue;
      }

      // Note that modelBox.getBoundingSphere() may not exist if the box is an LmvBox3.
      const modelValue = THREE.Box3.prototype.getBoundingSphere.call(modelBox, new THREE.Sphere()).radius * 0.001;
      value = Math.min(value, modelValue);
    }

    this.#raycaster.params.Line.threshold = value;
    this.#raycaster.params.PointCloud.threshold = value;
  }

  /**
   * Incrementally render some meshes until the tick budget is exhausted.
   *  @param {RenderBatchCB} renderBatchCB - Called that does the actual rendering. Called for each RenderBatch to be rendered.
   *  @param {number}   budgetRemaining - Remaining budget for this call. Batches when USE_OUT_OF_CORE_TILE_MANAGER
   *                                      is on; milliseconds on the legacy path.
   *  @param {number}   [cpuDeadline] - OOC only: absolute performance.now() CPU deadline (default Infinity).
   *                                    Ignored on the legacy path.
   *  @returns {number} Remaining budget after the call. Usually <=0 if the frame could not be fully finished yet.
   *
   * @callback RenderScene~renderBatchCB
   * @param {RenderBatch} finalRenderBatch
   */
  renderSome(renderBatchCB, budgetRemaining, cpuDeadline = Infinity) {
    if (!globals.USE_OUT_OF_CORE_TILE_MANAGER) {
      cpuDeadline = Infinity;
    }

    let budgetRemainingScaled = budgetRemaining;
    // Reference budget for the mid-frame fence and the WBOIT correction reset. Captured before the
    // loading-scale below so it reflects the full (unscaled) per-tick budget.
    const budgetAtStart = budgetRemaining;

    // While loading we limit the render budget for consecutive frames
    if (this.#renderBudgetScalingEnabled && !this.#allModelsLoaded && !this.#isFirstProgressiveFrame && !USE_GEOMETRY_OOC) {
      budgetRemainingScaled *= RENDER_BUDGET_SCALING_FACTOR_WHILE_LOADING;
    }

    let model = null;

    let fenceSet = false;
    let batchRenderStartTime = performance.now();
    const legacyTimingState = globals.USE_OUT_OF_CORE_TILE_MANAGER ? null : createLegacyBatchTimingState();
    let perFrameConsolidationBudget = globals.PER_FRAME_CONSOLIDATION_TIME_BUDGET;

    // OOC: separate per-batch CPU deadlines for consolidated vs non-consolidated meshes.
    // Consolidated batches are GPU-bound with less accurate time estimates, so they get a
    // looser emergency-stop limit. Non-consolidated batches are CPU-bound and expensive;
    // once enough consolidated batches are on screen, further non-consolidated draws use a
    // tighter deadline so remaining frame time goes to consolidation instead.
    let consolidatedCount = 0;
    let consolidatedDeadline = Infinity;
    let nonConsolidatedDeadline = Infinity;
    let nonConsolidatedAfterConsolidatedDeadline = Infinity;
    if (globals.USE_OUT_OF_CORE_TILE_MANAGER && cpuDeadline !== Infinity) {
      const cpuTimeRemaining = cpuDeadline - batchRenderStartTime;
      consolidatedDeadline = batchRenderStartTime + 2.0 * cpuTimeRemaining;
      nonConsolidatedDeadline = batchRenderStartTime + cpuTimeRemaining;
      nonConsolidatedAfterConsolidatedDeadline = batchRenderStartTime + 0.5 * cpuTimeRemaining;
    }

    const isPastDeadline = () => performance.now() >= cpuDeadline;

    const getNextBatch = (/** @type RenderModel */model) => {
      // If the CPU deadline is exceeded, exhaust the render budget to prevent this.#done from being set to true
      // in the finalRenderBatch code path. Without this, the consolidationDeadline could cause model.nextBatch
      // to return null, leading finalRenderBatch to be null and incorrectly assuming the rendering process is complete.
      if (globals.USE_OUT_OF_CORE_TILE_MANAGER && isPastDeadline()) {
        budgetRemainingScaled = BUDGET_EXHAUSTED;
        return null;
      }

      const now = performance.now();
      // We only allow perFrameConsolidationBudget ms of consolidation at each frame, but
      // still don't allow spending more than half of the remaining budget in consolidation
      // to prevent triggering an expensive consolidation at the end of the frame.
      const consolidationDeadline = globals.USE_OUT_OF_CORE_TILE_MANAGER ?
      consolidatedDeadline :
      now + Math.min(perFrameConsolidationBudget, 0.5 * budgetRemainingScaled);

      const traversalStartTime = now;

      let batch = model.nextBatch(globals.USE_OUT_OF_CORE_TILE_MANAGER ? cpuDeadline : Infinity, consolidationDeadline);
      let endTime = performance.now();
      let traversalTime = endTime - traversalStartTime;

      // Update the consolidation budget
      perFrameConsolidationBudget -= traversalTime;

      if (!globals.USE_OUT_OF_CORE_TILE_MANAGER) {
        budgetRemainingScaled -= traversalTime;
        // Reset start time, to not include consolidation time in render batch time estimation
        batchRenderStartTime = endTime;
      }

      if (globals.USE_OUT_OF_CORE_TILE_MANAGER && isPastDeadline()) {
        budgetRemainingScaled = BUDGET_EXHAUSTED;
      }

      return batch;
    };

    // repeat until render budget or CPU deadline is consumed...
    while (true) {
      // Find the best candidate render batch to render now -- in case there are multiple models.
      // TODO In case a huge number of models is loaded, we may have to rethink the linear loop below and use some priority heap or somesuch.
      let candidateIdx = 0;
      /** @type {RenderBatch|THREE.Scene|null} */let finalRenderBatch = null;

      if (this.interruptedRenderBatch) {
        // If a render batch in the previous frame was interrupted, we continue with this one.
        finalRenderBatch = this.interruptedRenderBatch;
        this.interruptedRenderBatch = undefined;
      } else {

        for (let iq = 0; iq < this.#candidateRenderBatches.length; iq++) {

          // candidate is the next RenderBatch to be processed from this._models[q]
          let candidateRenderBatch = this.#candidateRenderBatches[iq];
          model = this.#models[iq];

          // Note that the difference between null and undefined is important for RenderBatches here.
          //  - If it's null, it indicates that the traversal of this model is finished and any further getNextBatch() calls would be a waste of time.
          //    (which can be significant for large aggregates)
          //  - If it's undefined, it only means that it was interrupted with timeout (see RenderModel.nextBatch), so calling
          //    getNextBatch again is still necessary to properly finish rendering this model.
          if (!candidateRenderBatch && candidateRenderBatch !== null) {
            this.#candidateRenderBatches[iq] = candidateRenderBatch = getNextBatch(model);
          }

          if (budgetRemainingScaled <= 0 || globals.USE_OUT_OF_CORE_TILE_MANAGER && isPastDeadline()) {
            this.#isFirstProgressiveFrame = false;
            return budgetRemainingScaled;
          }

          // No more batches to render from this model
          if (candidateRenderBatch === null) {
            continue;
          }

          // If all previous candidates were null, candidateRenderBatch is obviously the best one so far.
          if (!finalRenderBatch) {
            candidateIdx = iq;
            finalRenderBatch = candidateRenderBatch;
          }

          // If final renderbatch and candidate have the same transparency, choose current candidate only if its renderImportance is higher.
          // The renderImportance of RenderBatches is set by model iterators.
          const chooseByRenderImportance = candidateRenderBatch.sortObjects == finalRenderBatch.sortObjects && candidateRenderBatch.renderImportance > finalRenderBatch.renderImportance;

          // if the renderbatch is transparent and the candidate is opaque, choose the candidate
          const mustReplaceTransparentRenderBatch = !candidateRenderBatch.sortObjects && finalRenderBatch.sortObjects;
          if (chooseByRenderImportance || mustReplaceTransparentRenderBatch) {
            candidateIdx = iq;
            finalRenderBatch = candidateRenderBatch;
          }
        }
      }

      // Render the batch we chose above and determine whether to continue the loop
      if (finalRenderBatch) {

        // Has the renderbatch been interrupted in the previous frame? (Note: can only be called if this function is provided
        // by the renderbatch. Renderbatches can also just be THREE.Scenes that do not have this interface)
        const interruptedInPreviousFrame = finalRenderBatch.hasBeenInterrupted && finalRenderBatch.hasBeenInterrupted();
        if (!interruptedInPreviousFrame) {
          // Fetch a new render batch from the model that we took the current batch from.
          this.#candidateRenderBatches[candidateIdx] = getNextBatch(this.#models[candidateIdx]);
        }

        if (globals.USE_OUT_OF_CORE_TILE_MANAGER && finalRenderBatch.setDeadline) {
          if (finalRenderBatch instanceof ConsolidatedRenderBatch) {
            finalRenderBatch.setDeadline(consolidatedDeadline);
            consolidatedCount++;
          } else {
            // Once enough consolidated batches are rendered, prefer spending the
            // remaining frame budget on consolidation rather than drawing more
            // non-consolidated geometry. When no background tasks ran last frame,
            // allow the full non-consolidated deadline again.
            if (consolidatedCount < 20 || !mainLoop.moreWorkToDoInExecuteTasks) {
              finalRenderBatch.setDeadline(nonConsolidatedDeadline);
            } else {
              finalRenderBatch.setDeadline(nonConsolidatedAfterConsolidatedDeadline);
            }
          }
        }

        // do the actual rendering
        finalRenderBatch.render ? finalRenderBatch.render(renderBatchCB) : renderBatchCB(finalRenderBatch);

        // Here we first check how much render budget has already been used. Since the size of the
        // batches in the initial, non-progressive frame is larger than the subsequent ones, a fence is set which
        // ensures that renderSome is not called again for the subsequent frame until this fence has been reached.
        const consumedBudget = budgetAtStart - budgetRemainingScaled;
        const fenceManager = this.#viewer.impl.fenceManager;

        // NOTE: The magic number in budgetFraction works well in all tested scenarios, but might not work well for
        // all types of models or systems. If this is the case we need to find an approach for a dynamic solution.
        const budgetFraction = 0.64;

        if (consumedBudget > budgetAtStart * budgetFraction && !fenceSet) {
          fenceManager.createFence('mid_frame');
          fenceSet = true;
        }

        if (!globals.USE_WEBGPU) {
          // Performing a flush after each rendered batch to ensure immediate execution of WebGL commands.
          // This helps prevent the command buffer from overflowing and reduces the risk of stalls. Frequent
          // flushes also helps in synchronizing between the CPU and GPU.
          this.#viewer.impl.renderer().flush();
        }

        if (finalRenderBatch.setDeadline) {
          // explicitly disable the deadline
          // We only want to use the deadline in the main render pass
          finalRenderBatch.setDeadline(undefined);
        }

        if (finalRenderBatch.hasBeenInterrupted && finalRenderBatch.hasBeenInterrupted()) {
          this.interruptedRenderBatch = finalRenderBatch;
          budgetRemainingScaled = globals.USE_OUT_OF_CORE_TILE_MANAGER ? BUDGET_EXHAUSTED : -1;
        } else {
          if (Object.prototype.hasOwnProperty.call(finalRenderBatch, "drawEnd")) {
            finalRenderBatch.drawEnd = finalRenderBatch.lastItem;
          }

          if (globals.USE_OUT_OF_CORE_TILE_MANAGER) {
            budgetRemainingScaled -= RENDER_BUDGET_PER_BATCH;
          } else {
            const batchRenderEndTime = performance.now();
            const measuredDelta = measureBatchDelta(batchRenderStartTime, batchRenderEndTime);
            batchRenderStartTime = batchRenderEndTime;
            budgetRemainingScaled -= deductLegacyBatchTimeCost(
              legacyTimingState,
              finalRenderBatch,
              measuredDelta,
              interruptedInPreviousFrame
            );
          }
        }

        // Check if we should exit the loop...
        if (budgetRemainingScaled <= 0 || globals.USE_OUT_OF_CORE_TILE_MANAGER && isPastDeadline()) {
          break;
        }
      } else {// thus, !finalRenderBatch
        // Skip the correction entirely when reusing the previous frame's accum, since it would clear the
        // persistent blend source and rebuild it for an identical result.
        if (!this.#doneOpaques && !this.#reuseTransparentAccum && (globals.USE_WBOIT || this.#transparentsNeedRedraw)) {
          this.#doneOpaques = true;
          this.resetTransparents();
          // Stop overwriting ID depth with opaque-only depth so depth writes accumulate across intermediate
          // frames. This is used for ID-only passes that render transparents as opaques for picking (WBOIT).
          globals.USE_WBOIT && this.#viewer.impl.glrenderer().setSkipIdDepthCopy(true);
          // Give the correction pass its own budget. The combined pass may have exhausted the render budget
          // entirely (especially in full-render mode where the whole scene renders in one tick). Without this,
          // the correction draws are skipped and the cleared WBOIT accum stays empty, so no transparency is visible.
          if (globals.USE_WBOIT && budgetRemainingScaled <= 0 && (!globals.USE_OUT_OF_CORE_TILE_MANAGER || !isPastDeadline())) {
            budgetRemainingScaled = budgetAtStart;
          }
        } else {
          // No more batches => Frame rendering finished, if all models are loaded
          this.#done = true;
          break;
        }
      }
    }

    if (!fenceSet) {
      const fenceManager = this.#viewer.impl.fenceManager;
      fenceManager.createFence('end_frame');

      if (!globals.USE_WEBGPU)
      this.#viewer.impl.renderer().flush();
    }

    this.#isFirstProgressiveFrame = false;

    return budgetRemainingScaled;
  }

  // TODO This method needs to be revisited as on demand loading is removed from the code base
  /** Resets the renderBatch traversal
   *   @param  {UnifiedCamera} camera
   *   @param  {number}        drawMode     - E.g., RENDER_NORMAL. See RenderFlags.js
   *   @param: {number}        [resetType]  - Must be one of RESET_NORMAL, RESET_REDRAW or RESET_RELOAD.
   *                                          Only used when on demand loading is enabled. RESET_RELOAD will reload and redraw geometry.
   *                                          RESET_REDRAW will redraw geometry. RESET_NORMAL will only redraw geometry that hasn't already been drawn.
   *                                          If undefined RESET_NORMAL is used.
   */
  reset(camera, drawMode, resetType, cutPlanes, cutplanesHideInterior = false) {
    // Reset the interrupted renderbatch, if there is one
    if (this.interruptedRenderBatch) {
      this.interruptedRenderBatch.resetInterrupted();
      this.interruptedRenderBatch = undefined;
    }

    this.#done = false;
    this.#doneOpaques = false;
    const mainTargetsCleared = this.#mainTargetsClearedBeforeReset;
    this.#mainTargetsClearedBeforeReset = false;

    // Calculate the viewing frustum
    // TODO same math is done in the renderer also. We could unify
    this.#frustum.reset(camera, cutPlanes, cutplanesHideInterior);
    this.#frustum.areaCullThreshold = globals.PIXEL_CULLING_THRESHOLD;

    if (!this.#models.length) {
      return;
    }

    this.#allModelsLoaded = this.areAllModelsLoaded();

    // Reuse the previous frame's WBOIT accum when geometry has settled and no main clear happened this frame.
    // A main clear only happens on a view change (camera/cutplanes), so its absence post-load means the same
    // view is re-rendered (OOC tile re-prioritization, a FINAL_FRAME listener) and the screen-space accum is
    // still pixel-correct, so redrawing it would clear+repopulate for no gain and strand the blend source empty.
    this.#reuseTransparentAccum = globals.USE_WBOIT && this.#allModelsLoaded && !mainTargetsCleared;

    // Whether to prime the transparent root into the combined heap (interleaved preview). Skipped when
    // reusing the accum (nothing transparent at all) OR in the non-progressive optimization (interleave
    // off, but the correction pass still runs). The correction itself is gated only by #reuseTransparentAccum.
    const primeTransparentHeap = !this.#reuseTransparentAccum && !this.#skipInterleavedTransparents;

    const consolidationDeadline = performance.now() + globals.PER_FRAME_CONSOLIDATION_TIME_BUDGET;
    const frameDeadline = globals.USE_OUT_OF_CORE_TILE_MANAGER ? performance.now() + globals.PER_FRAME_CONSOLIDATION_TIME_BUDGET : Infinity;

    // Begin the frustum based renderBatch iteration process per model.
    // A "Model" is all the objects to display. There's typically one model in a scene, so length is 1.
    for (let i = 0; i < this.#models.length; i++) {
      // decide what iterator to use, usually the BVH iterator
      this.#models[i].resetIterator(camera, this.#frustum, drawMode, resetType, primeTransparentHeap);

      // get the first RenderBatch (some set of fragments) to render.
      this.#candidateRenderBatches[i] = this.#models[i].nextBatch(frameDeadline, consolidationDeadline);

      this.#previousRenderBatches[i] = null;
    }
  }

  resetTransparents() {
    for (let i = 0; i < this.#models.length; i++) {
      this.#models[i].resetTransparents();
      this.#candidateRenderBatches[i] = undefined;
    }
    // Clear targets before re-drawing transparent frags.
    this.#viewer.impl.glrenderer().clearWboitTargets();
  }

  onClear() {
    this.#isFirstProgressiveFrame = true;
    this.#mainTargetsClearedBeforeReset = true;
  }

  // Freshly computed (not the #allModelsLoaded cache, which is only refreshed in reset()). Needed by callers that
  // run before reset() in a cycle, e.g., RenderCommandSystem deciding whether to clear the WBOIT accum.
  areAllModelsLoaded() {
    return this.#models.every((model) => model.isLoadDone());
  }

  isDone() {
    return this.#done || this.isEmpty();
  }

  ///////////////////////////////////////////////////////////////////////
  // Visibility and highlighting methods: see RenderModel.js for details.

  setAllVisibility(value) {
    for (let i = 0; i < this.#models.length; i++)
    this.#models[i].setAllVisibility(value);
  }

  hideLines(hide) {
    for (let i = 0; i < this.#models.length; i++)
    this.#models[i].hideLines(hide);
  }

  hidePoints(hide) {
    for (let i = 0; i < this.#models.length; i++)
    this.#models[i].hidePoints(hide);
  }

  hasHighlighted() {
    for (let i = 0; i < this.#models.length; i++)
    if (this.#models[i].hasHighlighted())
    return true;

    return false;
  }

  areAllVisible() {
    for (let i = 0; i < this.#models.length; i++)
    if (!this.#models[i].areAllVisible())
    return false;

    return true;
  }

  ///////////////////////////////////////////////////////////////////////

  areAll2D() {
    for (let i = 0; i < this.#models.length; i++)
    if (!this.#models[i].is2d())
    return false;

    return true;
  }

  areAll3D() {
    for (let i = 0; i < this.#models.length; i++)
    if (!this.#models[i].is3d())
    return false;

    return true;
  }

  /** Trigger bbox recomputation. See RenderModel.js for details. */
  invalidateVisibleBounds() {
    for (let i = 0; i < this.#models.length; i++)
    this.#models[i].invalidateBBoxes();
  }

  /**
  * @param {bool}            includeGhosted
  * @param {function(model)} [modeFilter]
  * @param {bool}            excludeShadow - Remove shadow geometry (if exists) from model bounds.
  * @returns {THREE.Box3}
  *
  * NOTE: The returned box object is always the same, i.e. later calls
  *       affect previously returned values. E.g., for
  *        let box1 = getVisibleBounds(true);
  *        let box2 = getVisibleBounds(false);
  *       the second call would also change box1.
  */
  getVisibleBounds(includeGhosted, bboxFilter, excludeShadow) {
    tmpBox.makeEmpty();
    for (let i = 0; i < this.#models.length; i++) {
      const model = this.#models[i];
      const modelBox = model.getVisibleBounds(includeGhosted, excludeShadow);

      // Consider bboxFilter
      let skipModel = bboxFilter && !bboxFilter(modelBox);
      if (skipModel) {
        continue;
      }

      tmpBox.union(modelBox);
    }
    return tmpBox;
  }

  /**
   * @param {THREE.Vector3} position            - Ray origin.
   * @param {THREE.Vector3} direction           - Ray direction.
   * @param {bool}          [ignoreTransparent] - Shoot trough transparent objects.
   * @param {number[]|number[][]} [dbIds]       - Optional filter of dbIds to be considered for testing. see RenderModel.rayIntersect().
   *                                              If modelIds is set, dbIds[i] must provide a separate dbId array for modelIds[i].
   * @param {number[]}      [modelIds]          - Optional list of modelIds to be considered for rayIntersection. (default is to consider all)
   * @param {Array}         [intersections]     - Optional return array with all found intersections.
   * @param {function}      [getDbIdAtPointFor2D] - Optional callback. For 2D models, to return the dbId and modelId in an array.
   * @param {Object}        [options]             - Rayintersection options (see RenderModel.rayIntersect)
   *
   * @returns {Object|null} Intersection result object (see RenderModel.rayIntersect)
   */
  // Add "meshes" parameter, after we get meshes of the object using id buffer,
  // then we just need to ray intersect this object instead of all objects of the model.
  rayIntersect(position, direction, ignoreTransparent, dbIds, modelIds, intersections, getDbIdAtPointFor2D, options) {
    // Init raycaster
    this.#raycaster.set(position, direction);

    // For multiple RenderModels, perform raytest on each of them and find the closest one.
    if (this.#models.length > 1) {
      // Collect raytest result objects from each 3D model
      const modelHits = [];

      if (modelIds) {
        for (let i = 0; i < modelIds.length; i++) {
          const model = this.findModel(modelIds[i]);
          if (model) {
            const modelDbIds = dbIds && dbIds[i];
            const res = model.rayIntersect(this.#raycaster, ignoreTransparent, modelDbIds, intersections, getDbIdAtPointFor2D, options);
            if (res) {
              modelHits.push(res);
            }
          }
        }
      } else {
        for (let i = 0; i < this.#models.length; i++) {
          // Perform raytest on model i
          const res = this.#models[i].rayIntersect(this.#raycaster, ignoreTransparent, dbIds, intersections, getDbIdAtPointFor2D, options);

          if (res) {
            modelHits.push(res);
          }
        }
      }

      if (!modelHits.length)
      return null;

      // Return closest hit
      modelHits.sort(function (a, b) {return a.distance - b.distance;});
      return modelHits[0];
    } else {
      // If we don't have any RenderModel, just return null.
      if (!this.#models.length)
      return null;

      // Apply modelIds filter
      const model = this.#models[0];
      if (modelIds && modelIds.indexOf(model.id) === -1) {
        return null;
      }

      // If we only have a single RenderModel, just call rayIntersect() on it.
      return model.rayIntersect(this.#raycaster, ignoreTransparent, dbIds, intersections, getDbIdAtPointFor2D, options);
    }
  }

  /**
   *  Progress of current frame rendering.
   *  @returns {number} Value in [0,1], where 1 means finished.
   */
  getRenderProgress() {
    return this.#models[0].getRenderProgress();
  }

  /** @returns {RenderModel[]} */
  getModels() {
    return this.#models;
  }

  /** @returns {RenderModel[]} */
  getHiddenModels() {
    return this.#hiddenModels;
  }

  /** @returns {RenderModel[]} */
  getAllModels() {
    return this.#models.concat(this.#hiddenModels);
  }

  // ----------------------------
  // Warning: The methods in the section below assume that there is exactly one RenderModel.
  //          They will ignore any additional models and cause an exception if the model list is empty.
  //

  // Direct access to FragmentList, GeometryList, and total number of RenderBatches.
  //
  // Note:
  //  - The methods do only care for model 0 and ignore any additional ones.
  //  - Will cause an error when called if the RenderModel array is empty.
  getFragmentList() {
    return this.#models[0].getFragmentList();
  }

  getGeometryList() {
    return this.#models[0].getGeometryList();
  }

  getSceneCount() {
    return this.#models[0].getSceneCount();
  }

  //Used by ground shadow update, ground reflection update, and screenshots
  getGeomScenes() {
    let scenes = [];
    for (let i = 0; i < this.#models.length; i++) {
      // Collect all scenes from next model
      const modelScenes = this.#models[i].getGeomScenes();
      for (let j = 0; j < modelScenes.length; j++) {
        // Some scenes may not exist. E.g., if it corresponds to an empty BVH node.
        const scene = modelScenes[j];
        if (scene) {
          scenes.push(scene);
        }
      }
    }
    return scenes;
  }

  // Used by ground shadow update, ground reflection update,
  getGeomScenesPerModel() {
    return this.#models.reduce((acc, m) => {
      acc.push(m.getGeomScenes());
      return acc;
    }, []);
  }

  // ---------------- End of section of functions without support for multiple RenderModels

  /** Sets animation transforms for all fragments to create an "exploded view": Each fragment is displaced
       * away from the model bbox center, so that you can distuinguish separate components.
       *
       * If the model data provides a model hierarchy (given via model.getData().instanceTree), it is also considered for the displacement.
       * In this case, we recursively shift each object away from the center of its parent node's bbox.
       *
       * @param {number} scale - In [0,1]. 0 means no displacement (= reset animation transforms).
       *                                   1 means maximum displacement, where the shift distance of an object varies
       *                                   depending on distance to model center and hierarchy level.
       * @param {Object} options - Additional setting for STRATEGY_HIERARCHY.
       * @param {Number} options.magnitude - Controls the spread of explode.
       * @param {Number} options.depthDampening - Controls the reduction of the explode effect with
       *                                          depth of the object in the hierarchy.
       */
  explode(scale, options = {}) {
    if (!this.#models.length) {
      return;
    }

    for (let q = 0; q < this.#models.length; q++) {
      const model = this.#models[q];
      ModelExploder.explode(model, scale, options);
    }

    this.invalidateVisibleBounds();
  }

  /**
   *  @params  {number} timeStamp
   *  @returns {bool}   true if any of the models needs a redraw
   */
  update(timeStamp) {
    // call update for all RenderModels and track
    // if any of these needs a redraw
    let needsRedraw = false;
    for (let q = 0; q < this.#models.length; q++) {
      const model = this.#models[q];
      needsRedraw = needsRedraw || model.update(timeStamp);
    }
    return needsRedraw;
  }

  /*
  *  Move model from visible models to hidden models
  *   @param {number} modelId - id of a currently visible model
  *   @returns {bool} true on success
  */
  hideModel(modelId) {
    // find model in the list of visible ones
    for (let i = 0; i < this.#models.length; i++) {
      const model = this.#models[i];
      if (model && model.id === modelId) {
        // move model from visible to hidden models
        this.removeModel(model);
        this.#hiddenModels.push(model);
        return true;
      }
    }
    // modelID does not refer to any visible model
    return false;
  }

  /*
  * Move previously hidden model to the array of rendered models.
  *  @param {number} modelId - id of a RenderModel in hiddenModels array
  *  @returns {bool} true on success
  */
  showModel(modelId) {
    // find model in list of hidden models
    for (let i = 0; i < this.#hiddenModels.length; ++i) {
      const model = this.#hiddenModels[i];
      if (model && model.id === modelId) {
        // mode model from hidden to visible models
        this.addModel(model);
        this.#hiddenModels.splice(i, 1);
        return true;
      }
    }
    // modelId does not refer to a hidden model
    return false;
  }

  setRenderBudgetScalingEnabled(enabled) {
    this.#renderBudgetScalingEnabled = enabled;
  }

  /** @param {boolean} value */
  setTransparentsNeedRedraw(value) {
    this.#transparentsNeedRedraw = value;
  }

  setSkipInterleavedTransparents(value) {
    this.#skipInterleavedTransparents = value;
  }
}